import { useState, useCallback } from 'react';
import {
  isVoltageSectionEnabled,
  isCurrentSectionEnabled,
} from '@/utils/compass_configuration_helper/compass_configuration_helper';

const VOLTAGE_CHIP_COUNT = 22;
const CURRENT_CHIP_COUNT = 11;
// Channel floor for the SA dual-thumb range (1-based UI labels for both channels).
const VOLTAGE_MIN_BOUND = 1;
const CURRENT_MIN_BOUND = 1;

// UI-library-agnostic thumb identifier; SmartAdaptiveRangeSlider translates
// MUI's 0/1 thumb index into this before calling the hook.
export type RangeThumb = 'min' | 'max';

// Clamps min <= max and min != max. On collision, pulls back only the
// dragged thumb (activeThumb) so the untouched one never moves; falls back
// to nudging max up (or min down at the ceiling) when activeThumb is unknown.
function clampRangeApart(
  min: number,
  max: number,
  floor: number,
  ceiling: number,
  activeThumb?: RangeThumb,
): [number, number] {
  // Clamp to bounds before separating: separating first (then clamping) can
  // push an already out-of-range side further out and lose the other side's
  // clamp, producing an inverted [lo, hi]. See PR #650 review comment.
  let lo = Math.max(floor, Math.min(ceiling, Math.min(min, max)));
  let hi = Math.max(floor, Math.min(ceiling, Math.max(min, max)));
  if (lo === hi) {
    if (activeThumb === 'min' && hi > floor) lo = hi - 1;
    else if (activeThumb === 'max' && lo < ceiling) hi = lo + 1;
    else if (hi < ceiling) hi += 1;
    else lo -= 1;
  }
  return [lo, hi];
}

export interface PvdConfigFieldValues {
  specName: string;
  voltage: boolean[];
  current: boolean[];
  adaptiveVoltageAllowed: boolean;
  adaptiveCurrentAllowed: boolean;

  autoAdaptIsLocked: boolean;
  autoAdaptLockedToValue: boolean;

  voltageBubbleSizeIsLocked: boolean;
  voltageBubbleSizeLockedToValue: boolean;
  voltageMinSetting: number;
  voltageMaxSetting: number;

  currentBubbleSizeIsLocked: boolean;
  currentBubbleSizeLockedToValue: boolean;
  currentMinSetting: number;
  currentMaxSetting: number;
}

/**
 * Owns every editable field on the PVD configuration document, plus a set of
 * intent-revealing predicate helpers for the modal components to consume.
 *
 * The two channels' Auto-Adapt selectors share a single underlying field
 * pair (`autoAdapt`*) per spec §4.4 — both channel expansions render their
 * own selector when SA is on for that channel, but they bind to the same
 * state here. This enforces "sync rendering" at the hook level.
 */
export function usePvdConfigFields(initialValues: PvdConfigFieldValues) {
  const [specName, setSpecName] = useState(initialValues.specName);

  const [voltage, setVoltage] = useState<boolean[]>([...initialValues.voltage]);
  const [current, setCurrent] = useState<boolean[]>([...initialValues.current]);

  const [adaptiveVoltageAllowed, setAdaptiveVoltageAllowed] = useState(initialValues.adaptiveVoltageAllowed);
  const [adaptiveCurrentAllowed, setAdaptiveCurrentAllowed] = useState(initialValues.adaptiveCurrentAllowed);

  const [autoAdaptIsLocked, setAutoAdaptIsLockedState] = useState(initialValues.autoAdaptIsLocked);
  const [autoAdaptLockedToValue, setAutoAdaptLockedToValueState] = useState(initialValues.autoAdaptLockedToValue);

  const [voltageBubbleSizeIsLocked, setVoltageBubbleSizeIsLockedState] = useState(initialValues.voltageBubbleSizeIsLocked);
  const [voltageBubbleSizeLockedToValue, setVoltageBubbleSizeLockedToValueState] = useState(initialValues.voltageBubbleSizeLockedToValue);
  const [voltageMinSetting, setVoltageMinSettingState] = useState(initialValues.voltageMinSetting);
  const [voltageMaxSetting, setVoltageMaxSettingState] = useState(initialValues.voltageMaxSetting);

  const [currentBubbleSizeIsLocked, setCurrentBubbleSizeIsLockedState] = useState(initialValues.currentBubbleSizeIsLocked);
  const [currentBubbleSizeLockedToValue, setCurrentBubbleSizeLockedToValueState] = useState(initialValues.currentBubbleSizeLockedToValue);
  const [currentMinSetting, setCurrentMinSettingState] = useState(initialValues.currentMinSetting);
  const [currentMaxSetting, setCurrentMaxSettingState] = useState(initialValues.currentMaxSetting);

  const isVoltageEnabled = useCallback(
    () => isVoltageSectionEnabled(voltage, adaptiveVoltageAllowed),
    [voltage, adaptiveVoltageAllowed],
  );

  const isCurrentEnabled = useCallback(
    () => isCurrentSectionEnabled(current, adaptiveCurrentAllowed),
    [current, adaptiveCurrentAllowed],
  );

  const toggleVoltageChip = useCallback((index: number) => {
    setVoltage((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  const toggleCurrentChip = useCallback((index: number) => {
    setCurrent((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  const enableAllVoltage = useCallback(() => setVoltage(Array(VOLTAGE_CHIP_COUNT).fill(true)), []);
  const disableAllVoltage = useCallback(() => setVoltage(Array(VOLTAGE_CHIP_COUNT).fill(false)), []);
  const enableAllCurrent = useCallback(() => setCurrent(Array(CURRENT_CHIP_COUNT).fill(true)), []);
  const disableAllCurrent = useCallback(() => setCurrent(Array(CURRENT_CHIP_COUNT).fill(false)), []);

  // Auto-Adapt three-state pill row. The voltage and current channel
  // expansions both render this selector but they bind to the same fields.
  const setAutoAdaptUserDefined = useCallback(() => {
    setAutoAdaptIsLockedState(false);
  }, []);
  const setAutoAdaptOn = useCallback(() => {
    setAutoAdaptIsLockedState(true);
    setAutoAdaptLockedToValueState(true);
  }, []);
  const setAutoAdaptOff = useCallback(() => {
    setAutoAdaptIsLockedState(true);
    setAutoAdaptLockedToValueState(false);
  }, []);

  // Voltage Bubble Size three-state. Per Spec D18 `locked_to_value` is bool:
  //   false = small (was wire 0), true = large (was wire 1).
  // The User Defined path leaves locked_to_value untouched (`is_locked = false`
  // makes the value irrelevant on the wire) so round-tripping a previously
  // saved value during edit-then-cancel-then-re-edit stays stable.
  const setVoltageBubbleUserDefined = useCallback(() => {
    setVoltageBubbleSizeIsLockedState(false);
  }, []);
  const setVoltageBubbleSmall = useCallback(() => {
    setVoltageBubbleSizeIsLockedState(true);
    setVoltageBubbleSizeLockedToValueState(false);
  }, []);
  const setVoltageBubbleLarge = useCallback(() => {
    setVoltageBubbleSizeIsLockedState(true);
    setVoltageBubbleSizeLockedToValueState(true);
  }, []);

  // Current Bubble Size three-state, same shape as voltage above. Bool per D18.
  const setCurrentBubbleUserDefined = useCallback(() => {
    setCurrentBubbleSizeIsLockedState(false);
  }, []);
  const setCurrentBubbleSmall = useCallback(() => {
    setCurrentBubbleSizeIsLockedState(true);
    setCurrentBubbleSizeLockedToValueState(false);
  }, []);
  const setCurrentBubbleLarge = useCallback(() => {
    setCurrentBubbleSizeIsLockedState(true);
    setCurrentBubbleSizeLockedToValueState(true);
  }, []);

  const setVoltageRange = useCallback((min: number, max: number, activeThumb?: RangeThumb) => {
    const [lo, hi] = clampRangeApart(min, max, VOLTAGE_MIN_BOUND, VOLTAGE_CHIP_COUNT, activeThumb);
    setVoltageMinSettingState(lo);
    setVoltageMaxSettingState(hi);
  }, []);
  const setCurrentRange = useCallback((min: number, max: number, activeThumb?: RangeThumb) => {
    const [lo, hi] = clampRangeApart(min, max, CURRENT_MIN_BOUND, CURRENT_CHIP_COUNT, activeThumb);
    setCurrentMinSettingState(lo);
    setCurrentMaxSettingState(hi);
  }, []);

  return {
    specName,
    setSpecName,

    voltage,
    current,
    adaptiveVoltageAllowed,
    setAdaptiveVoltageAllowed,
    adaptiveCurrentAllowed,
    setAdaptiveCurrentAllowed,

    autoAdaptIsLocked,
    autoAdaptLockedToValue,
    setAutoAdaptUserDefined,
    setAutoAdaptOn,
    setAutoAdaptOff,

    voltageBubbleSizeIsLocked,
    voltageBubbleSizeLockedToValue,
    setVoltageBubbleUserDefined,
    setVoltageBubbleSmall,
    setVoltageBubbleLarge,

    voltageMinSetting,
    voltageMaxSetting,
    setVoltageRange,

    currentBubbleSizeIsLocked,
    currentBubbleSizeLockedToValue,
    setCurrentBubbleUserDefined,
    setCurrentBubbleSmall,
    setCurrentBubbleLarge,

    currentMinSetting,
    currentMaxSetting,
    setCurrentRange,

    toggleVoltageChip,
    toggleCurrentChip,
    enableAllVoltage,
    disableAllVoltage,
    enableAllCurrent,
    disableAllCurrent,

    isVoltageEnabled,
    isCurrentEnabled,
  };
}

export type PvdConfigFieldsReturn = ReturnType<typeof usePvdConfigFields>;
